import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import i18n from '@/lib/i18n';
import { customerSchema } from '@/lib/schemas';

/**
 * מראה את CreateCustomerDto בשרת: name חובה (1–200), והשאר אופציונלי —
 * email (פורמט, עד 320), phone (עד 40), address (עד 500).
 * שדה ריק בטופס הוא "לא נשלח", ולכן `''` מותר כאן ומושמט ב-submit.
 */
const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, i18n.t('customers:form.nameRequired'))
    .max(200, i18n.t('customers:form.nameMax')),
  email: z
    .string()
    .trim()
    .max(320, i18n.t('customers:form.emailMax320'))
    .email(i18n.t('customers:form.emailInvalid'))
    .or(z.literal('')),
  phone: z.string().trim().max(40, i18n.t('customers:form.phoneMax')),
  address: z.string().trim().max(500, i18n.t('customers:form.addressMax')),
});
type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = { name: '', email: '', phone: '', address: '' };

export function NewCustomerDialog() {
  const { t } = useTranslation(['customers', 'common']);
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      request('/customers', {
        method: 'POST',
        // השרת מנרמל אימייל ל-lowercase לצורך ההתאמה מול Gmail sync;
        // נורמול גם כאן מונע יצירת כפילות מהמסך.
        body: {
          name: values.name.trim(),
          ...(values.email ? { email: values.email.trim().toLowerCase() } : {}),
          ...(values.phone ? { phone: values.phone.trim() } : {}),
          ...(values.address ? { address: values.address.trim() } : {}),
        },
        schema: customerSchema,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (customer) => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(t('new.created'), customer.name);
      reset(EMPTY);
      setOpen(false);
    },
    onError: (err: unknown) => {
      toast.error(
        t('new.failed'),
        err instanceof ApiError ? err.message : t('form.unexpectedError'),
      );
    },
  });

  const onSubmit = handleSubmit((values) => create.mutateAsync(values).catch(() => undefined));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset(EMPTY);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" data-track="customer.new">
          <Plus aria-hidden />
          {t('new.trigger')}
        </Button>
      </DialogTrigger>

      <DialogContent aria-describedby="new-customer-desc">
        <DialogHeader>
          <DialogTitle>{t('new.title')}</DialogTitle>
          <DialogDescription id="new-customer-desc">
            {t('new.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <DialogBody>
            <Field label={t('form.name')} htmlFor="customer-name" error={errors.name?.message} required>
              <Input
                id="customer-name"
                autoComplete="off"
                invalid={!!errors.name}
                aria-describedby={errors.name ? 'customer-name-error' : undefined}
                {...register('name')}
              />
            </Field>

            <Field
              label={t('form.email')}
              htmlFor="customer-email"
              error={errors.email?.message}
              hint={t('new.emailHint')}
            >
              <Input
                id="customer-email"
                type="email"
                autoComplete="off"
                invalid={!!errors.email}
                aria-describedby={
                  errors.email ? 'customer-email-error' : 'customer-email-hint'
                }
                {...register('email')}
              />
            </Field>

            <Field label={t('form.phone')} htmlFor="customer-phone" error={errors.phone?.message}>
              <Input
                id="customer-phone"
                type="tel"
                autoComplete="off"
                invalid={!!errors.phone}
                aria-describedby={errors.phone ? 'customer-phone-error' : undefined}
                {...register('phone')}
              />
            </Field>

            <Field label={t('form.address')} htmlFor="customer-address" error={errors.address?.message}>
              <Input
                id="customer-address"
                autoComplete="off"
                invalid={!!errors.address}
                aria-describedby={errors.address ? 'customer-address-error' : undefined}
                {...register('address')}
              />
            </Field>
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" loading={create.isPending}>
              {t('new.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
